// ============================================================================
// PayTypeModal — pay type and work state, the two facts that decide where
// somebody appears and which state's new-hire packet they get.
//
// Two RPCs, called only for the field that actually changed, so an unchanged
// field never writes an audit row saying it did.
// ============================================================================
import { useState, type FormEvent } from 'react';
import { X as XIcon } from 'lucide-react';
import { hrSetPayType, hrSetWorkState } from './hooks/use-hr-data';
import { overlayClickClose } from '../../shared/overlay-close';
import { errorMessage } from '../../shared/errors';

const PAY_TYPES = ['Salaried', 'Hourly'];

interface Props {
  employeeId: string;
  payType: string | null;
  workState: string | null;
  onClose: () => void;
  onSaved: () => void;
}

export default function PayTypeModal({
  employeeId,
  payType,
  workState,
  onClose,
  onSaved,
}: Props) {
  const [pay, setPay] = useState(payType ?? '');
  const [state, setState] = useState(workState ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const payChanged = (pay || null) !== (payType || null);
  const stateChanged = (state.toUpperCase() || null) !== (workState || null);

  async function handleSubmit(ev: FormEvent) {
    ev.preventDefault();
    const st = state.trim().toUpperCase();
    if (st !== '' && !/^[A-Z]{2}$/.test(st)) {
      setError('Work state must be a two-letter code, e.g. KS.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      if (payChanged) await hrSetPayType(employeeId, pay || null);
      if (stateChanged) await hrSetWorkState(employeeId, st || null, 'manual');
      onSaved();
      onClose();
    } catch (err) {
      console.error('[hr] set pay type / work state failed', err);
      setError(errorMessage(err, 'Could not save the change.'));
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end justify-center bg-tcs-scrim/60 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-label="Set pay type and work state"
      {...overlayClickClose(onClose)}
    >
      <form
        onClick={(ev) => ev.stopPropagation()}
        onSubmit={handleSubmit}
        className="w-full max-w-md rounded-t-xl bg-tcs-overlay p-5 shadow-xl sm:rounded-xl"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-tcs-text">Pay type &amp; work state</h2>
            <p className="text-xs text-tcs-text-muted">{employeeId}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-tcs-text-muted hover:bg-tcs-surface"
          >
            <XIcon className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-4 space-y-4">
          <Field label="Pay type">
            <select value={pay} onChange={(ev) => setPay(ev.target.value)} className={inputCls}>
              <option value="">Not set</option>
              {PAY_TYPES.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
            <span className="mt-1 block normal-case text-tcs-text-muted">
              Decides which group they appear in, and whether the directory can say anything about
              benefits eligibility. 1099s and subcontractors get a pay type too &mdash; that is how
              they show up under Hourly paid.
            </span>
          </Field>

          <Field label="Work state">
            <input
              value={state}
              onChange={(ev) => setState(ev.target.value.toUpperCase().slice(0, 2))}
              placeholder="KS"
              maxLength={2}
              className={`${inputCls} uppercase`}
            />
            <span className="mt-1 block normal-case text-tcs-text-muted">
              Chooses the state forms in their onboarding packet. Overrides the home address.
            </span>
          </Field>
        </div>

        {error && (
          <p className="mt-3 rounded-md border border-st-danger-border bg-st-danger-bg p-2 text-xs text-st-danger-fg-strong">
            {error}
          </p>
        )}

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-tcs-border px-3 py-2 text-sm text-tcs-text-secondary hover:bg-tcs-bg-muted"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting || (!payChanged && !stateChanged)}
            className="rounded-md bg-tcs-inverse px-4 py-2 text-sm font-medium text-tcs-inverse-ink hover:bg-tcs-inverse-hover disabled:opacity-50"
          >
            {submitting ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
}

const inputCls =
  'w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-base focus:border-tcs-border-input focus:outline-none';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-xs">
      <span className="mb-1 block font-medium uppercase tracking-wide text-tcs-text-muted">
        {label}
      </span>
      {children}
    </label>
  );
}
